import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchNotifications,
  fetchNotificationById,
  markNotificationRead
} from "../../../../features/notifications/notificationThunks";
import NotificationDetail from "./NotificationDetail";
import "./notifications.css";

const Notifications = () => {
  const dispatch = useDispatch();
  const { list, selected, loading } = useSelector(state => state.notifications);

  useEffect(() => {
    dispatch(fetchNotifications());
  }, [dispatch]);

  // Helper function for "Time Ago"
  const getTimeAgo = (dateString) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffInSeconds = Math.floor((now - past) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return past.toLocaleDateString();
  };

  const openNotification = (id) => {
    dispatch(fetchNotificationById(id));
    dispatch(markNotificationRead(id));
  };

  return (
    <div className="notification-container">
      <div className="notification-header-section">
        <h2 className="page-title">Notifications</h2>
        <span className="count-badge">{list.length} Total</span>
      </div>

      <div className="notification-list">
        {list.length === 0 && !loading && (
          <div className="empty-state">
            <p>No  Available  Notification! </p>
          </div>
        )}

        {list.map((item) => (
          <div
  key={item._id}
  className={`list-item ${item.status === "unread" ? "unread-status" : ""}`}
  onClick={() => openNotification(item._id)}
>
  {/* Thumbnail Image */}
  <div className="item-thumbnail">
    <img
      src={
        item.image
          ? `${process.env.REACT_APP_IMAGE_BASE_URL.replace(/\/$/, "")}${item.image}`
          : "https://via.placeholder.com/80x80?text=No+Image"
      }
      alt={item.title}
    />
  </div>
            <div className="item-indicator"></div>
            
            <div className="item-body">
              <div className="item-header">
                <h4 className="item-title">{item.title}</h4>
                <span className="item-time">{getTimeAgo(item.createdAt)}</span>
              </div>
              <p className="item-preview">
                {item.message.length > 80 
                  ? `${item.message.substring(0, 80)}...` 
                  : item.message}
              </p>
            </div>

            <div className="chevron-icon">›</div>
          </div>
        ))}
      </div>

      {selected && <NotificationDetail />}
      <style>
        {`
        /* Container Layout */
.notification-container {
  max-width: 800px;
  margin: 2rem auto;
  padding: 0 1rem;
  font-family: 'Inter', -apple-system, sans-serif;
}

.notification-header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 800;
  color: #111827;
}

.count-badge {
  background: #f3f4f6;
  color: #4b5563;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

/* List Styling */
.notification-list {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.list-item {
  display: flex;
  align-items: flex-start;
  padding: 1.25rem;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  transition: all 0.2s ease;
  position: relative;
}

.list-item:last-child {
  border-bottom: none;
}

.list-item:hover {
  background-color: #f9fafb;
}

/* Unread State */
.unread-status {
  background-color: #f0f7ff; /* Very light blue */
}

.unread-status:hover {
  background-color: #e0efff;
}

.unread-status .item-indicator {
  background-color: #3b82f6; /* Blue dot */
}

/* Indicator Dot */
.item-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 6px;
  margin-right: 16px;
  flex-shrink: 0;
  background-color: transparent;
}

/* Item Content */
.item-body {
  flex-grow: 1;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 4px;
}

.item-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
}

.unread-status .item-title {
  font-weight: 700;
}

.item-time {
  font-size: 0.8rem;
  color: #9ca3af;
  white-space: nowrap;
  margin-left: 10px;
}

.item-preview {
  margin: 0;
  font-size: 0.9rem;
  color: #6b7280;
  line-height: 1.4;
}

.chevron-icon {
  margin-left: 12px;
  color: #d1d5db;
  font-size: 1.5rem;
  line-height: 1;
  align-self: center;
}

/* Empty State */
.empty-state {
  padding: 3rem;
  text-align: center;
  color: #9ca3af;
}

/* Responsive Design */
@media (max-width: 640px) {
  .notification-container {
    margin: 1rem auto;
  }

  .item-header {
    flex-direction: column;
    gap: 2px;
  }

  .item-time {
    margin-left: 0;
  }

  .list-item {
    padding: 1rem;
  }
}

/* Thumbnail Image */
.item-thumbnail {
  width: 52px;
  height: 52px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  margin-right: 14px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* Mobile adjustment */
@media (max-width: 640px) {
  .item-thumbnail {
    width: 44px;
    height: 44px;
  }
}
        `}
      </style>
    </div>
  );
};

export default Notifications;